import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  Save,
  Send,
  CheckCircle,
  XCircle,
  Download,
  RefreshCw,
  Edit3,
  FileText,
  Calendar,
  BookOpen,
  AlertCircle,
  Loader2,
  Sparkles,
  Clock,
  ThumbsUp,
  MessageSquare,
  AlertTriangle,
  X,
  Target,
  Users,
  Package
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PlanStudioReview = () => {
  const { id: planId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user has @yomev.org email
  const isYomevUser = user?.email?.toLowerCase().endsWith('@yomev.org');
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refining, setRefining] = useState(false);
  const [error, setError] = useState('');
  
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState(null);
  
  const [refineModal, setRefineModal] = useState(false);
  const [refineSections, setRefineSections] = useState([]);
  const [refineFeedback, setRefineFeedback] = useState('');
  
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbacks, setFeedbacks] = useState([]);
  const [pdfPreviewModal, setPdfPreviewModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Normalize content field names from AI output
  const normalizeContent = (content) => {
    if (!content) return content;
    const normalized = { ...content };
    const fieldMappings = {
      'mainYetkinlik': 'mainCompetency',
      'mainYetkinlikArea': 'mainCompetencyArea',
      'supportYetkinlik': 'supportCompetency',
      'supportYetkinlikArea': 'supportCompetencyArea',
      'Materyaller': 'materials',
      'materyaller': 'materials',
      'kelimeHavuzu': 'vocabularyPool',
      'haftalikHedef': 'weeklyObjective',
      'anaSoru': 'mainQuestion',
      'haftaninRitueli': 'weeklyRitual',
      'haftalikYonerge': 'weeklyDirective',
      'gunlukPlanlar': 'dailyPlans',
      'miniRubrik': 'miniRubric',
      'haftaninKaniti': 'weeklyEvidence',
    };
    
    // Normalize top-level fields
    for (const [oldKey, newKey] of Object.entries(fieldMappings)) {
      if (normalized[oldKey] !== undefined && normalized[newKey] === undefined) {
        normalized[newKey] = normalized[oldKey];
        delete normalized[oldKey];
      }
    }
    
    // Normalize dailyPlans field names
    if (normalized.dailyPlans && Array.isArray(normalized.dailyPlans)) {
      normalized.dailyPlans = normalized.dailyPlans.map(dayPlan => {
        const normalizedDay = { ...dayPlan };
        const dayFieldMappings = {
          'EtkinlikTitle': 'activityTitle',
          'etkinlikTitle': 'activityTitle',
          'EtkinlikDescription': 'activityDescription',
          'etkinlikDescription': 'activityDescription',
          'gunSonuSorusu': 'endOfDayQuestion',
          'disiplinlerArasiOgrenme': 'interdisciplinaryLearning',
          'farkliLastirma': 'differentiation',
        };
        for (const [oldKey, newKey] of Object.entries(dayFieldMappings)) {
          if (normalizedDay[oldKey] !== undefined && normalizedDay[newKey] === undefined) {
            normalizedDay[newKey] = normalizedDay[oldKey];
            delete normalizedDay[oldKey];
          }
        }
        return normalizedDay;
      });
    }
    
    return normalized;
  };

  useEffect(() => {
    if (!isYomevUser) {
      navigate('/plan-studio');
      return;
    }
    fetchPlan();
  }, [planId, isYomevUser]);

  const fetchPlan = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/plan-studio/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPlan(data);
        // Normalize content JSON field names
        const normalizedContent = normalizeContent(data.contentJson);
        setEditedContent(normalizedContent);
        
        // Fetch feedbacks
        const feedbackRes = await fetch(`${API}/api/plan-studio/plans/${planId}/feedback`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (feedbackRes.ok) {
          setFeedbacks(await feedbackRes.json());
        }
      } else {
        setError('Plan bulunamadı');
      }
    } catch (error) {
      setError('Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/plan-studio/plans/${planId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editedContent)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setPlan(updated);
        setEditMode(false);
      } else {
        const err = await res.json();
        setError(err.detail || 'Kaydetme hatası');
      }
    } catch (error) {
      setError('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleRefine = async () => {
    if (refineSections.length === 0) return;
    
    setRefining(true);
    setRefineModal(false);
    
    try {
      const res = await fetch(`${API}/api/plan-studio/plans/${planId}/refine`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sections: refineSections,
          feedback: refineFeedback
        })
      });
      
      if (res.ok) {
        // Start polling for completion
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`${API}/api/plan-studio/plans/${planId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (statusRes.ok) {
              const updatedPlan = await statusRes.json();
              
              if (updatedPlan.generation_status === 'completed') {
                clearInterval(pollInterval);
                setPlan(updatedPlan);
                const normalizedContent = normalizeContent(updatedPlan.contentJson);
                setEditedContent(normalizedContent);
                setRefineSections([]);
                setRefineFeedback('');
                setRefining(false);
              } else if (updatedPlan.generation_status === 'failed') {
                clearInterval(pollInterval);
                setError('AI yenileme başarısız oldu. Lütfen tekrar deneyin.');
                setRefining(false);
              }
              // If still 'refining', continue polling
            }
          } catch (e) {
            console.error('Polling error:', e);
          }
        }, 2000); // Poll every 2 seconds
        
        // Timeout after 60 seconds
        setTimeout(() => {
          clearInterval(pollInterval);
          if (refining) {
            setError('Yenileme zaman aşımına uğradı. Sayfa yenilenince kontrol edin.');
            setRefining(false);
          }
        }, 60000);
        
      } else {
        const err = await res.json();
        setError(err.detail || 'Yenileme hatası');
        setRefining(false);
      }
    } catch (error) {
      setError('Bağlantı hatası');
      setRefining(false);
    }
  };

  const handleStatusChange = async (action) => {
    try {
      const res = await fetch(`${API}/api/plan-studio/plans/${planId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const updated = await res.json();
        setPlan(updated);
      } else {
        const err = await res.json();
        setError(err.detail || 'İşlem hatası');
      }
    } catch (error) {
      setError('Bağlantı hatası');
    }
  };

  const handleFeedbackSubmit = async () => {
    try {
      const res = await fetch(`${API}/api/plan-studio/plans/${planId}/feedback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          generatedPlanId: planId,
          rating: feedbackRating,
          comment: feedbackComment,
          feedbackType: 'genel'
        })
      });
      
      if (res.ok) {
        const newFeedback = await res.json();
        setFeedbacks([newFeedback, ...feedbacks]);
        setFeedbackModal(false);
        setFeedbackComment('');
      }
    } catch (error) {
      setError('Geri bildirim gönderilemedi');
    }
  };

  const handleDownloadPdf = async () => {
    if (!plan?.pdfUrl) return;
    
    setDownloadingPdf(true);
    try {
      // If it's a direct URL, open in new tab
      window.open(plan.pdfUrl, '_blank');
    } catch (error) {
      setError('PDF indirilemedi');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleRegeneratePdf = async () => {
    if (!plan?.contentJson) return;
    
    setDownloadingPdf(true);
    try {
      const res = await fetch(`${API}/api/plan-studio/plans/${planId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.pdfUrl) {
          window.open(data.pdfUrl, '_blank');
        }
      } else {
        setError('PDF oluşturulamadı');
      }
    } catch (error) {
      setError('PDF indirilemedi');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Taslak' },
      in_review: { color: 'bg-yellow-100 text-yellow-700', label: 'İncelemede' },
      approved: { color: 'bg-green-100 text-green-700', label: 'Onaylandı' },
      published: { color: 'bg-blue-100 text-blue-700', label: 'Yayında' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Reddedildi' }
    }[status] || { color: 'bg-gray-100 text-gray-700', label: status };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !plan) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900">{error}</h2>
        <button
          onClick={() => navigate('/plan-studio')}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          Plan Studio'ya Dön
        </button>
      </div>
    );
  }

  const content = editMode ? editedContent : normalizeContent(plan?.contentJson);

  return (
    <div className="space-y-6" data-testid="plan-studio-review">
      {/* Refining Overlay */}
      {refining && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md text-center shadow-xl">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AI ile Yenileniyor...</h3>
            <p className="text-gray-600 text-sm">
              Plan bölümleri AI tarafından yeniden oluşturuluyor. Bu işlem 30-60 saniye sürebilir.
            </p>
            <div className="mt-4 flex justify-center gap-1">
              <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
              <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
              <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/plan-studio')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft size={20} />
            Plan Studio'ya Dön
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {content?.theme || `${plan?.year}. Yıl - ${plan?.week}. Hafta Planı`}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            {getStatusBadge(plan?.status)}
            <span className="text-sm text-gray-500">
              {plan?.year}. Yıl / {plan?.week}. Hafta
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              plan?.planType === 'CORE' ? 'bg-purple-100 text-purple-700' :
              plan?.planType === 'TURKCE' ? 'bg-blue-100 text-blue-700' :
              'bg-orange-100 text-orange-700'
            }`}>
              {plan?.planType === 'CORE' ? 'Temel' : 
               plan?.planType === 'TURKCE' ? 'Türkçe' : 'Matematik'}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* PDF Actions */}
          {plan?.pdfUrl ? (
            <>
              <button
                onClick={() => setPdfPreviewModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
                data-testid="preview-pdf-btn"
              >
                <FileText size={18} />
                PDF Önizle
              </button>
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50"
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                PDF İndir
              </button>
            </>
          ) : plan?.contentJson ? (
            <button
              onClick={handleRegeneratePdf}
              disabled={downloadingPdf}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50"
              data-testid="generate-pdf-btn"
            >
              {downloadingPdf ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
              PDF Oluştur
            </button>
          ) : null}
          
          {plan?.status === 'draft' && (
            <>
              <button
                onClick={() => setRefineModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100"
                data-testid="refine-btn"
              >
                <Sparkles size={18} />
                AI ile Yenile
              </button>
              <button
                onClick={() => setEditMode(!editMode)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
                  editMode ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="edit-btn"
              >
                <Edit3 size={18} />
                {editMode ? 'Düzenlemeyi İptal' : 'Düzenle'}
              </button>
              {editMode && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="save-btn"
                >
                  {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  Kaydet
                </button>
              )}
              <button
                onClick={() => handleStatusChange('submit')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                data-testid="submit-btn"
              >
                <Send size={18} />
                İncelemeye Gönder
              </button>
            </>
          )}
          
          {plan?.status === 'in_review' && (
            <>
              <button
                onClick={() => handleStatusChange('approve')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                data-testid="approve-btn"
              >
                <CheckCircle size={18} />
                Onayla
              </button>
              <button
                onClick={() => {
                  const reason = prompt('Red sebebini yazın (en az 10 karakter):');
                  if (reason && reason.length >= 10) {
                    fetch(`${API}/api/plan-studio/plans/${planId}/reject?reason=${encodeURIComponent(reason)}`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` }
                    }).then(res => res.ok && fetchPlan());
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                data-testid="reject-btn"
              >
                <XCircle size={18} />
                Reddet
              </button>
            </>
          )}
          
          {plan?.status === 'approved' && (
            <button
              onClick={() => handleStatusChange('publish')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="publish-btn"
            >
              <Send size={18} />
              Yayınla
            </button>
          )}
          
          <button
            onClick={() => setFeedbackModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            data-testid="feedback-btn"
          >
            <MessageSquare size={18} />
            Geri Bildirim
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {/* Plan Content */}
      {content ? (
        <div className="space-y-6">
          {/* Meta Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <BookOpen size={18} className="text-blue-600" />
                Ana Kazanım
              </h3>
              {editMode ? (
                <textarea
                  value={editedContent?.mainCompetency || ''}
                  onChange={(e) => setEditedContent({...editedContent, mainCompetency: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  rows={2}
                />
              ) : (
                <p className="text-gray-600 text-sm">{content.mainCompetency || '-'}</p>
              )}
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <Target size={18} className="text-purple-600" />
                Haftalık Hedef
              </h3>
              {editMode ? (
                <textarea
                  value={editedContent?.weeklyObjective || editedContent?.weeklyDirective || ''}
                  onChange={(e) => setEditedContent({...editedContent, weeklyObjective: e.target.value, weeklyDirective: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  rows={2}
                />
              ) : (
                <p className="text-gray-600 text-sm">{content.weeklyObjective || content.weeklyDirective || '-'}</p>
              )}
            </div>
          </div>

          {/* Main Question & Ritual */}
          {(content.mainQuestion || content.weeklyRitual) && (
            <div className="grid md:grid-cols-2 gap-4">
              {content.mainQuestion && (
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                  <h4 className="font-medium text-amber-800 mb-2 text-sm">Ana Soru</h4>
                  <p className="text-amber-700 text-sm italic">"{content.mainQuestion}"</p>
                </div>
              )}
              {content.weeklyRitual && (
                <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
                  <h4 className="font-medium text-purple-800 mb-2 text-sm">Haftanın Ritüeli</h4>
                  <p className="text-purple-700 text-sm">{content.weeklyRitual}</p>
                </div>
              )}
            </div>
          )}

          {/* Sub Competencies & Development Areas & Materials */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h4 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
                <BookOpen size={14} className="text-blue-500" />
                Alt Kazanımlar
              </h4>
              {content.subCompetencies?.length > 0 ? (
                <ul className="text-sm text-gray-600 space-y-1">
                  {content.subCompetencies.map((sc, idx) => (
                    <li key={idx} className="flex items-start gap-1">
                      <span className="text-blue-500">•</span> {sc}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400 text-sm">-</p>
              )}
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h4 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
                <Users size={14} className="text-purple-500" />
                Gelişim Alanları
              </h4>
              {content.developmentAreas?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {content.developmentAreas.map((da, idx) => (
                    <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                      {da}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">-</p>
              )}
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h4 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
                <Package size={14} className="text-green-500" />
                Materyaller
              </h4>
              {content.materials?.length > 0 ? (
                <ul className="text-sm text-gray-600 space-y-1">
                  {content.materials.slice(0, 6).map((m, idx) => (
                    <li key={idx} className="flex items-start gap-1">
                      <span className="text-green-500">•</span> {m}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400 text-sm">-</p>
              )}
            </div>
          </div>

          {/* Vocabulary Pool */}
          {content.vocabularyPool?.length > 0 && (
            <div className="bg-green-50 rounded-xl border border-green-200 p-4">
              <h4 className="font-medium text-green-800 mb-2 text-sm">Kelime / Kavram Havuzu</h4>
              <div className="flex flex-wrap gap-2">
                {content.vocabularyPool.map((word, idx) => (
                  <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                    {word}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Daily Plans Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Günlük Planlar</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    <th className="px-3 py-3 text-left text-sm font-medium w-24">Bölüm</th>
                    {dayNames.map((day) => (
                      <th key={day} className="px-4 py-3 text-center text-sm font-medium" style={{width: '17%'}}>
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Row 1: Activity Title & Description */}
                  <tr className="bg-white">
                    <td className="px-3 py-3 border-r border-gray-200 bg-blue-50 font-medium text-sm text-blue-800 align-top">
                      Etkinlik
                    </td>
                    {dayNames.map((dayName) => {
                      const dayPlan = content.dailyPlans?.find(d => 
                        d.day?.toLowerCase() === dayName.toLowerCase()
                      );
                      return (
                        <td key={dayName} className="px-3 py-3 border-r border-gray-200 align-top text-sm">
                          {dayPlan ? (
                            <div>
                              <div className="font-medium text-gray-900">{dayPlan.activityTitle || '-'}</div>
                              <p className="text-gray-600 text-xs mt-1 line-clamp-3">{dayPlan.activityDescription || ''}</p>
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Row 2: Interdisciplinary Learning */}
                  <tr className="bg-gray-50">
                    <td className="px-3 py-3 border-r border-gray-200 bg-purple-50 font-medium text-sm text-purple-800 align-top">
                      Disiplinler Arası
                    </td>
                    {dayNames.map((dayName) => {
                      const dayPlan = content.dailyPlans?.find(d => 
                        d.day?.toLowerCase() === dayName.toLowerCase()
                      );
                      const inter = dayPlan?.interdisciplinaryLearning;
                      return (
                        <td key={dayName} className="px-3 py-3 border-r border-gray-200 align-top text-xs">
                          {inter && Object.keys(inter).length > 0 ? (
                            <div className="space-y-1">
                              {Object.entries(inter).slice(0, 3).map(([area, desc], idx) => (
                                <div key={idx}>
                                  <span className="font-medium text-purple-700">{area}:</span>{' '}
                                  <span className="text-gray-600">{String(desc).slice(0, 50)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Row 3: Differentiation */}
                  <tr className="bg-white">
                    <td className="px-3 py-3 border-r border-gray-200 bg-green-50 font-medium text-sm text-green-800 align-top">
                      Farklılaştırma
                    </td>
                    {dayNames.map((dayName) => {
                      const dayPlan = content.dailyPlans?.find(d => 
                        d.day?.toLowerCase() === dayName.toLowerCase()
                      );
                      const diff = dayPlan?.differentiation;
                      return (
                        <td key={dayName} className="px-3 py-3 border-r border-gray-200 align-top text-xs">
                          {diff && (diff.supportNeeded || diff.advanced) ? (
                            <div className="space-y-1">
                              {diff.supportNeeded && (
                                <div>
                                  <span className="font-medium text-orange-600">Destek:</span>{' '}
                                  <span className="text-gray-600">{diff.supportNeeded.slice(0, 40)}</span>
                                </div>
                              )}
                              {diff.advanced && (
                                <div>
                                  <span className="font-medium text-blue-600">İleri:</span>{' '}
                                  <span className="text-gray-600">{diff.advanced.slice(0, 40)}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Row 4: End of Day Question */}
                  <tr className="bg-gray-50">
                    <td className="px-3 py-3 border-r border-gray-200 bg-amber-50 font-medium text-sm text-amber-800 align-top">
                      Gün Sonu Sorusu
                    </td>
                    {dayNames.map((dayName) => {
                      const dayPlan = content.dailyPlans?.find(d => 
                        d.day?.toLowerCase() === dayName.toLowerCase()
                      );
                      return (
                        <td key={dayName} className="px-3 py-3 border-r border-gray-200 align-top text-xs">
                          {dayPlan?.endOfDayQuestion ? (
                            <span className="italic text-gray-700">"{dayPlan.endOfDayQuestion.slice(0, 60)}{dayPlan.endOfDayQuestion.length > 60 ? '...' : ''}"</span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Mini Rubric */}
          {content.miniRubric && (content.miniRubric.developing || content.miniRubric.expected || content.miniRubric.advanced) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Mini Rubrik (3 Seviye)</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                    <span className="font-medium text-yellow-800 text-sm">Gelişiyor</span>
                  </div>
                  <p className="text-yellow-700 text-xs">{content.miniRubric.developing || '-'}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                    <span className="font-medium text-green-800 text-sm">Beklenen</span>
                  </div>
                  <p className="text-green-700 text-xs">{content.miniRubric.expected || '-'}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    <span className="font-medium text-blue-800 text-sm">İleri</span>
                  </div>
                  <p className="text-blue-700 text-xs">{content.miniRubric.advanced || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Weekly Evidence */}
          {content.weeklyEvidence && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
              <h4 className="font-medium text-amber-800 mb-2">Haftanın Kanıtı (Portfolyo)</h4>
              <p className="text-sm text-amber-700">{content.weeklyEvidence}</p>
              {content.weeklyEvidenceDescription && (
                <p className="text-xs text-amber-600 mt-2">{content.weeklyEvidenceDescription}</p>
              )}
            </div>
          )}

          {/* Evaluation Notes */}
          {content.evaluationNotes && (
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
              <h4 className="font-medium text-blue-800 mb-2">Değerlendirme Notları</h4>
              <p className="text-sm text-blue-700">{content.evaluationNotes}</p>
            </div>
          )}

          {/* Special Days */}
          {content.specialDays?.length > 0 && (
            <div className="bg-pink-50 rounded-xl border border-pink-200 p-4">
              <h4 className="font-medium text-pink-800 mb-2">Özel Günler</h4>
              <div className="flex flex-wrap gap-2">
                {content.specialDays.map((day, idx) => (
                  <span key={idx} className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">
                    {day}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Plan içeriği henüz oluşturulmamış veya bir hata oluştu.</p>
        </div>
      )}

      {/* Feedbacks */}
      {feedbacks.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Geri Bildirimler</h3>
          <div className="space-y-3">
            {feedbacks.map((fb) => (
              <div key={fb.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <ThumbsUp
                        key={i}
                        size={14}
                        className={i < fb.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(fb.createdAt).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                {fb.comment && <p className="text-sm text-gray-700">{fb.comment}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Refine Modal */}
      {refineModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI ile Bölümleri Yenile
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yenilenecek Bölümleri Seçin
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['theme', 'weeklyObjective', 'day_1', 'day_2', 'day_3', 'day_4', 'day_5'].map((section) => (
                  <label key={section} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={refineSections.includes(section)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRefineSections([...refineSections, section]);
                        } else {
                          setRefineSections(refineSections.filter(s => s !== section));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">
                      {section === 'theme' ? 'Tema' :
                       section === 'weeklyObjective' ? 'Haftalık Hedef' :
                       `${dayNames[parseInt(section.split('_')[1]) - 1]}`}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Geri Bildirim (Opsiyonel)
              </label>
              <textarea
                value={refineFeedback}
                onChange={(e) => setRefineFeedback(e.target.value)}
                placeholder="AI'ya nasıl iyileştirmeler yapması gerektiğini açıklayın..."
                className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRefineModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                onClick={handleRefine}
                disabled={refining || refineSections.length === 0}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {refining ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                {refining ? 'Yenileniyor...' : 'Yenile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {feedbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Geri Bildirim Ver</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Puan</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => setFeedbackRating(rating)}
                    className={`p-2 rounded-lg ${
                      feedbackRating >= rating ? 'text-yellow-500' : 'text-gray-300'
                    }`}
                  >
                    <ThumbsUp size={24} className={feedbackRating >= rating ? 'fill-yellow-500' : ''} />
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Yorum</label>
              <textarea
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="Planla ilgili düşünceleriniz..."
                className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setFeedbackModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                onClick={handleFeedbackSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Gönder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfPreviewModal && plan?.pdfUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">PDF Önizleme</h3>
              <button
                onClick={() => setPdfPreviewModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="text-center py-8">
              <FileText className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-6">
                PDF dosyası tarayıcının güvenlik ayarları nedeniyle burada önizlenemiyor.
                PDF'i görüntülemek için aşağıdaki butonu kullanın.
              </p>
              
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    window.open(plan.pdfUrl, '_blank');
                    setPdfPreviewModal(false);
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <FileText size={18} />
                  Yeni Sekmede Aç
                </button>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = plan.pdfUrl;
                    link.download = `plan_${plan.year}y_${plan.week}h.pdf`;
                    link.click();
                    setPdfPreviewModal(false);
                  }}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Download size={18} />
                  İndir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanStudioReview;
